// components/EnhancedMeducateLogoLoader.jsx
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import { useEffect, useState } from "react";
import pharmationLogo from "assets/images/pharmation-logo-light.svg";

// ==============================|| ENHANCED MEDUCATE LOGO LOADER ||============================== //

export default function EnhancedMeducateLogoLoader() {
  const theme = useTheme();
  const [loaded, setLoaded] = useState(false);

  // Trigger animation after component mount
  useEffect(() => {
    setLoaded(true);
  }, []);

  // Fade in animation
  const fadeIn = keyframes`
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  `;

  // Gentle pulse animation for the circles
  const circlePulse = keyframes`
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.02);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 0.8;
    }
  `;

  // Very subtle rotation for the dashed circle
  const subtleRotation = keyframes`
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  `;


  // Animation for dots
  const dotPulse = keyframes`
    0% {
      transform: scale(1);
      opacity: 0.6;
    }
    50% {
      transform: scale(1.5);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 0.6;
    }
  `;

  // Floating dots animation
  const float = keyframes`
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
    100% {
      transform: translateY(0);
    }
  `;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 9999,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(0, 0, 0, 0.92)"
            : "rgba(255, 255, 255, 0.97)",
        animation: `${fadeIn} 0.5s ease-out`,
        overflow: "hidden",
      }}
    >
      {/* Outer solid circle with pulse animation */}
      <Box
        sx={{
          position: "absolute",
          width: "280px",
          height: "280px",
          borderRadius: "50%",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          animation: `${circlePulse} 4s infinite ease-in-out`,
        }}
      />

      {/* Dashed circle - middle with rotation */}
      <Box
        sx={{
          position: "absolute",
          width: "220px",
          height: "220px",
          borderRadius: "50%",
          border: "1px dashed rgba(0, 0, 0, 0.08)",
          animation: `${subtleRotation} 40s infinite linear, ${circlePulse} 5s infinite ease-in-out`,
          animationDelay: "0s, 0.5s",
        }}
      />

      {/* Inner solid white circle with shadow and pulse */}
      <Box
        sx={{
          position: "absolute",
          width: "160px",
          height: "160px",
          borderRadius: "50%",
          backgroundColor: "white",
          animation: `${circlePulse} 6s infinite ease-in-out`,
          animationDelay: "1s",
        }}
      />

      {/* Small dots positioned around the circles with animations */}
      <Box
        sx={{
          position: "absolute",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#FECC00",
          top: "calc(50% - 140px)",
          left: "50%",
          animation: `${dotPulse} 3s infinite ease-in-out, ${float} 6s infinite ease-in-out`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#FECC00",
          top: "calc(50% - 80px)",
          left: "calc(50% - 120px)",
          animation: `${dotPulse} 3s infinite ease-in-out, ${float} 6s infinite ease-in-out`,
          animationDelay: "0.5s, 1s",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#FECC00",
          bottom: "calc(50% - 100px)",
          left: "calc(50% - 110px)",
          animation: `${dotPulse} 3s infinite ease-in-out, ${float} 6s infinite ease-in-out`,
          animationDelay: "1s, 2s",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#0049E0",
          bottom: "calc(50% - 140px)",
          right: "calc(50% - 120px)",
          animation: `${dotPulse} 3s infinite ease-in-out, ${float} 6s infinite ease-in-out`,
          animationDelay: "1.5s, 3s",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#0049E0",
          top: "calc(50% + 80px)",
          right: "calc(50% - 130px)",
          animation: `${dotPulse} 3s infinite ease-in-out, ${float} 6s infinite ease-in-out`,
          animationDelay: "2s, 4s",
        }}
      />

      {/* Original SVG logo with individual pill animations - ENLARGED */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transform: loaded ? "scale(1)" : "scale(0.8)", // Increased scale for larger pills
          transition: "transform 0.5s ease-out",
        }}
      >
        <svg
          viewBox="493 370 514 350"
          width="120"
          height="82"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <image href={pharmationLogo} width="1500" height="1500" />
        </svg>
      </Box>
    </Box>
  );
}
